import { useState, type ReactNode } from 'react'
import { CheckCircle, XCircle, Rows } from '@phosphor-icons/react'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { Banner, Button, Card, CardBody } from '@/ui'
import { ActionBar } from '../../components/GateScreen'
import {
  closeChecks,
  firstFailing,
  type CloseCheck,
  type CloseCheckKey,
} from '../../model/closeChecks'
import { cancelPending, lotPlan, visibleSteps, type StepId } from '../../model/delivery'
import { useWizard } from './context'
import { SeasonLightDot } from './StepDetails'
import { useLotFigures, useSeasonLight } from './hooks'
import { PlainScanButton } from '../../components/Scan'
import { BiText } from '../../components/GateScreen'

const CHECK_KEYS: Record<CloseCheckKey, MessageKey> = {
  storage: 'gate.check.storage',
  bags_counted: 'gate.check.bagsCounted',
  failed_bags: 'gate.check.failedBagsOk',
  probe_draws: 'gate.check.probeOk',
  accepted: 'gate.check.accepted',
  tags: 'gate.check.tags',
  weights: 'gate.check.weights',
  moisture: 'gate.check.moisture',
  sample: 'gate.check.sample',
  disputes: 'gate.check.disputes',
  agreed: 'gate.check.agreed',
  slip_no: 'gate.check.slipNo',
  slip_photo: 'gate.check.slipPhoto',
  // V1 checks (the V1 Final check screen draws its own; listed so the map is total)
  bags_tagged: 'gate.check.bagsTagged',
  sealed: 'gate.check.sealed',
  cards: 'gate.check.cards',
  location: 'gate.check.location',
}
const CHECK_STEP: Record<CloseCheckKey, StepId> = {
  storage: 'details',
  bags_counted: 'check',
  failed_bags: 'check',
  probe_draws: 'check',
  accepted: 'price',
  tags: 'tag',
  weights: 'tag',
  moisture: 'moisture',
  sample: 'sample',
  disputes: 'tag',
  agreed: 'slip',
  slip_no: 'slip',
  slip_photo: 'slip',
  bags_tagged: 'tag',
  sealed: 'sample',
  cards: 'price',
  location: 'close',
}

function useCloseChecks() {
  const { d, crop, cards } = useWizard()
  const plan = lotPlan(d)
  const cardsOk = plan.every((l) => cards[l.stream] && cards[l.stream]!.price_paise_per_kg != null)
  return closeChecks(d, {
    moistureRequired: crop?.moisture_required ?? false,
    sampleMinG: crop?.sample_min_g ?? 500,
    cardsOk,
  })
}

function useCheckText() {
  const { t } = useI18n()
  return (c: CloseCheck) => t(CHECK_KEYS[c.key], c.vars)
}

/** F4.10 — डिलीवरी बंद करें · Close delivery: every tick green, one action for one or two lots. */
export function StepClose({ onClose, closing }: { onClose: () => void; closing: boolean }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const ctx = useWizard()
  const { d, dispatch } = ctx
  const checks = useCloseChecks()
  const text = useCheckText()
  const fail = firstFailing(checks)
  const lots = useLotFigures()
  const light = useSeasonLight(ctx)
  const steps = visibleSteps(d)
  const goodBags = lots
    .filter((l) => l.plan.stream === 'MAIN')
    .reduce((s, l) => s + l.plan.bag_count, 0)
  const redBags = lots
    .filter((l) => l.plan.stream === 'CONV')
    .reduce((s, l) => s + l.plan.bag_count, 0)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-2">
          <ul className="flex flex-col">
            {checks
              .filter((c) => steps.includes(CHECK_STEP[c.key]))
              .map((c, i) => (
                <li
                  key={`${c.key}-${i}`}
                  className="flex items-center gap-2 border-b border-rule/60 py-2 last:border-b-0"
                >
                  {c.ok ? (
                    <CheckCircle
                      size={22}
                      weight="fill"
                      className="shrink-0 text-ok"
                      aria-hidden="true"
                    />
                  ) : (
                    <XCircle
                      size={22}
                      weight="fill"
                      className="shrink-0 text-danger"
                      aria-hidden="true"
                    />
                  )}
                  <span className={`flex-1 text-[14px] ${c.ok ? '' : 'font-semibold text-danger'}`}>
                    {text(c)}
                  </span>
                  {!c.ok && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dispatch({ type: 'goto', step: CHECK_STEP[c.key] })}
                    >
                      {t('gate.close.fix')}
                    </Button>
                  )}
                </li>
              ))}
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-1.5">
          {lots.map((l) => (
            <div key={l.plan.id} className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[14px]">{l.plan.code}</span>
              <span className="text-[14px]">
                {t(l.plan.stream === 'MAIN' ? 'gate.lot.goodBags' : 'gate.lot.redBags', {
                  count: l.plan.bag_count,
                })}{' '}
                · <span className="font-mono">{fmt.weight(l.figures.net_g)}</span>
              </span>
            </div>
          ))}
        </CardBody>
      </Card>
      {light && <SeasonLightDot light={light} />}
      {light === 'AMBER' && <Banner tone="warn">{t('gate.close.amberWaits')}</Banner>}
      {d.slip.disputed && <Banner tone="warn">{t('gate.close.disputedHeld')}</Banner>}
      <Banner tone="info" title={t('gate.close.row')}>
        <span className="inline-flex items-center gap-1.5">
          <Rows aria-hidden="true" />
          {redBags > 0
            ? t('gate.close.rowBoth', { good: goodBags, red: redBags })
            : t('gate.close.rowOne', { n: goodBags || redBags })}
        </span>
      </Banner>

      <ActionBar>
        {fail && <p className="text-center text-[13px] font-semibold text-danger">{text(fail)}</p>}
        <Button size="xl" fullWidth disabled={!!fail} loading={closing} onClick={onClose}>
          <BiText k="gate.close.close" />
        </Button>
      </ActionBar>
    </div>
  )
}

/**
 * Cancel delivery (F4.10): every tag already on a bag and the seal must be cut and scanned void
 * first; then void_delivery goes with them. MOVED_TO_PAPER (F4-B3.b) keeps the tags on the bags.
 */
export function CancelFlow({
  onSend,
  sending,
  header,
}: {
  onSend: () => void
  sending: boolean
  header?: ReactNode
}) {
  const { t } = useI18n()
  const { d, dispatch, stamp } = useWizard()
  const pending = cancelPending(d)
  const all = [...pending.tags, ...(pending.seal ? [pending.seal] : [])]
  const reasonKey: MessageKey =
    d.cancel?.reason === 'DIVERTED_TO_DRY'
      ? 'gate.cancel.diverted'
      : d.cancel?.reason === 'MOVED_TO_PAPER'
        ? 'gate.cancel.movedToPaper'
        : 'gate.cancel.walkAway'
  const [last, setLast] = useState<string | null>(null)
  // Once a tag on a bag is cut, the delivery cannot go back to the main path.
  const cutStarted = d.voided_tags.some((v) => v.void_reason === 'WALK_AWAY')
  return (
    <div className="flex flex-col gap-4">
      {header}
      <Banner tone="warn" title={t(reasonKey)}>
        {all.length > 0 ? t('gate.cancel.scanEach', { count: all.length }) : t('gate.cancel.ready')}
      </Banner>
      {all.length > 0 && (
        <>
          <ul className="flex flex-col gap-1">
            {all.map((p) => (
              <li key={p} className="rounded-chip bg-paper-sunk px-3 py-2 font-mono text-[15px]">
                {p}
              </li>
            ))}
          </ul>
          {last && <Banner tone="success">{t('gate.tag.voided', { code: last })}</Banner>}
          <PlainScanButton
            label={t('gate.cancel.scanVoid')}
            validate={(c) => (all.includes(c) ? null : t('gate.cancel.notOnList'))}
            onScan={(p) => {
              dispatch({
                type: 'void_tag',
                payload: p,
                kind: p === pending.seal ? 'SEAL' : 'BAG',
                reason: 'WALK_AWAY',
                at: stamp(),
              })
              setLast(p)
            }}
          />
        </>
      )}
      <ActionBar>
        <Button
          size="xl"
          variant="danger"
          fullWidth
          disabled={all.length > 0}
          loading={sending}
          onClick={onSend}
        >
          {t('gate.cancel.send')}
        </Button>
        {!cutStarted && (
          <Button
            size="touch"
            variant="ghost"
            fullWidth
            onClick={() => dispatch({ type: 'abort_cancel' })}
          >
            {t('gate.cancel.keep')}
          </Button>
        )}
      </ActionBar>
    </div>
  )
}
